import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import _ from 'lodash';
import moment from 'moment';
import { t } from '../../components/common/Translate';
import { Auth, recordActions } from '../../config/configuration/react';

export const useCalculation = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const appLang = Auth.preferredLanguage().toUpperCase();
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(false);
  const isError = useSelector((state) => state.reduxRecords.error);
  const recordId = useSelector((state) => state.reduxRecords.currentRecord);
  const supervisorAuthorized = useSelector(
    (state) => state.reduxRecords.data[recordId]?.supervisor?.authorization || 0,
  );
  const supervisorMaxMileageEntryStockDate = useSelector(
    (state) => state.reduxRecords.data[recordId]?.valuation?.maxMileageEntryStockDate,
  );
  const b2bCounterAppraisalRequired = useSelector(
    (state) => state.reduxRecords.data[recordId]?.supervisor?.counterAppraisalRequired,
  );
  const supervisorOffer = useSelector((state) => state.reduxRecords.data[recordId])?.supervisor?.c2bOffer || '';
  const supervisorComment = useSelector((state) => state.reduxRecords.data[recordId])?.supervisor?.comment;
  const recordStatus = useSelector((state) => state.reduxRecords.data[recordId]?.record?.statusId);
  const enableCounterAppraisal = useSelector(
    (state) => state.reduxRecords.data[recordId]?.sourceInfos?.sourceGroupData?.enableCounterAppraisal,
  );
  const enableManualDecision = useSelector(
    (state) => state.reduxRecords.data[recordId]?.sourceInfos?.sourceGroupData?.enableManualDecision,
  );
  const [purchaseAllow, setPurchaseAllow] = useState(supervisorAuthorized);
  const [counterAppraisalAllow, setCounterAppraisalAllow] = useState(() => {
    if (b2bCounterAppraisalRequired === true) return 1;
    if (b2bCounterAppraisalRequired === false) return 0;
    return '';
  });
  const [buyerOffer, setBuyerOffer] = useState(supervisorOffer);
  const [buyerComment, setBuyerComment] = useState(supervisorComment);
  const rotation = useSelector((state) => state.reduxRecords.data[recordId]?.quotation?._salesSpeed?.rotation);
  const autobiz = useSelector((state) => state.reduxRecords.data[recordId]?.quotation);
  let versionId = useSelector((state) => state.reduxRecords.data[recordId]?.vehicle?.versionId);
  let versionName = useSelector((state) => state.reduxRecords.data[recordId]?.vehicle?.versionList);
  const mileage = useSelector((state) => state.reduxRecords.data[recordId]?.vehicle?.mileage);
  const userPricing = useSelector((state) => state.reduxRecords.data[recordId]?.userPricing);
  const refurbishmentCostCarcheck = _.round(
    useSelector((state) => state.reduxRecords.data[recordId]?.expertiseCosts?.mdCosts),
  );
  const versionsAvailable = useSelector((state) => state.reduxRecords.data[recordId]?.vehicle?.versionsData) || [];
  const recordCountryId = useSelector((state) => state?.reduxRecords?.data[recordId]?.record?.countryId);
  const registrationDate = useSelector((state) => state?.reduxRecords?.data[recordId]?.vehicle?.registrationDate?.date);
  const meetingDate = useSelector((state) => state?.reduxRecords?.data[recordId]?.appointment?.appointmentDateTime);
  const entryStockDate = useSelector((state) => state?.reduxRecords?.data[recordId]?.supervisor?.entryStockDate?.date);
  const parseDate = (dateString) => {
    if (!dateString) return null;
    const normalizedDate = dateString.replace(' ', 'T').split('.')[0];
    return new Date(normalizedDate);
  };
  const parsedRegistrationDate = parseDate(registrationDate);
  const parsedMeetingDate = parseDate(meetingDate);
  const parsedEntryStockDate = parseDate(entryStockDate);
  const diffBetweenMeetingAndRegistration =
    parsedMeetingDate && parsedRegistrationDate
      ? Math.floor((parsedMeetingDate - parsedRegistrationDate) / (1000 * 60 * 60 * 24))
      : 0;
  const diffBetweenEntryAndMeeting =
    parsedEntryStockDate && parsedMeetingDate
      ? Math.floor((parsedEntryStockDate - parsedMeetingDate) / (1000 * 60 * 60 * 24))
      : 0;
  const calculateMaxMileageEntryStockDate = () => {
    if (!mileage || diffBetweenMeetingAndRegistration === 0) return mileage || 0;
    return Math.round(mileage + (mileage / diffBetweenMeetingAndRegistration) * diffBetweenEntryAndMeeting);
  };
  const [maxMileageEntryStockDate, setMaxMileageEntryStockDate] = useState(
    supervisorMaxMileageEntryStockDate ?? calculateMaxMileageEntryStockDate(),
  );
  const dateFormat = appLang === 'DE' ? 'DD.MM.YYYY' : 'DD/MM/YYYY';
  const handleCreate = async () => {
    setIsLoading(true);
    if (!versionId) {
      versionsAvailable.forEach((v, key) => {
        if (key === 0) {
          versionId = v.id;
          versionName = v.name;
        }
      });
    }
    const params = {};
    params.versionId = versionId;
    params.versionName = versionName;
    params.mileage = mileage;
    params.correctedPrice = autobiz?.marketValue;
    params.correctedPriceUser = userPricing.marketValue;
    params.authorization = purchaseAllow;
    params.maxMileageEntryStockDate = maxMileageEntryStockDate ? parseInt(maxMileageEntryStockDate, 10) : null;
    params.counterAppraisalRequired = counterAppraisalAllow ? Number(counterAppraisalAllow) : null;
    params.c2bOffer =
      (parseFloat(buyerOffer.replace(/[^a-zA-Z^0-9.]/g, '')) > 0 &&
        parseFloat(buyerOffer.replace(/[^a-zA-Z^0-9.]/g, ''))) ||
      null;
    params.comment = buyerComment;
    params.recordId = recordId;
    params.profile = userPricing.carProfile;
    params.rotationAutobiz = rotation;
    params.rotation = userPricing.plannedDelay;
    params.refurbishmentCostAutobiz = refurbishmentCostCarcheck;
    params.refurbishmentCost = parseInt(userPricing?.refurbishingCosts, 10);
    params.tradeIn = autobiz?.tradeInUser;
    params.entryStockValue = userPricing.entryStockKey;
    params.entryStockDate = userPricing.entryStockDate;
    params.suggestedEntryStockDate = userPricing.entryStockDate;
    params.pricingAutobizResult = autobiz?.pricingAutobizResult;
    params.pricingUserResult = userPricing.pricingUserResult;
    if (Auth.country() === 'ES') {
      params.tva = userPricing.vatSpain;
      params.pricingEtAutobiz = autobiz?.prixEt;
      params.pricingEt = userPricing.prixEtUser;
    }
    const id = await dispatch(recordActions.offerPricing(recordId, params));
    if (id) {
      setIsLoading(false);
      if (recordStatus >= 3) {
        const pricingHistory = await dispatch(recordActions.fetchPricingHistory(recordId));
        setMessage(
          `${t('buyoutOfferMessage')} ${moment(pricingHistory[pricingHistory?.length - 1].createdAt).format(
            dateFormat,
          )} ${t('to')} ${moment(pricingHistory[pricingHistory?.length - 1].createdAt).format('HH:mm')}`,
        );
        if (recordStatus === 3) {
          setMessage(
            `${t('buyoutFirstOfferMessage')} ${moment(pricingHistory[0].createdAt).format(dateFormat)} ${t(
              'to',
            )} ${moment(pricingHistory[0].createdAt).format('HH:mm')}`,
          );
        }
      }
    }
    history.push(`/files/${recordId}`);
  };

  const formatValue = (v) => {
    const value = v.replace(',', '.').replace(/[^\d.\d]/g, '');
    if (value !== '') {
      return setBuyerOffer(value);
    }
    return setBuyerOffer('');
  };

  const openAutobizTradeLink = () => {
    history.push({
      pathname: `/files/${recordId}/pushAt`,
      state: {
        ...history.location.state,
      },
    });
  };

  return {
    purchaseAllow,
    setPurchaseAllow,
    maxMileageEntryStockDate,
    setMaxMileageEntryStockDate,
    counterAppraisalAllow,
    setCounterAppraisalAllow,
    buyerOffer,
    setBuyerOffer: (v) => formatValue(v),
    buyerComment,
    setBuyerComment,
    handleCreate,
    isLoading,
    isError,
    openAutobizTradeLink,
    message,
    enableCounterAppraisal,
    enableManualDecision,
    recordStatus,
    recordCountryId,
  };
};
